import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import { useUsers } from "../../../hooks/useUsers";
import UserTable from "../../../components/UserTable";
import { postForceLogoutUser } from "../../../api/client";
import AddUserForm from "../../../components/AddUserForm";
import { usePageContext } from "../../../context/PageContext";
import { useAuthContext } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { formatForceLogoutError } from "../../../utils/forceLogoutUi";
import {
  useEffectivePermissions,
  effectiveScopeSetForUser,
  can,
} from "../../../hooks/useEffectivePermissions";
import UserDeletionDialog from "./UserDeletionDialog";

const Users = () => {
  const { t } = useTranslation("settings");
  const { userData } = useAuthContext();
  const { data: permsData } = useEffectivePermissions();
  const isMasterUser = Number(userData?.master) === 1;
  const userScopeSet = useMemo(
    () => effectiveScopeSetForUser(permsData?.scopes, { isMaster: isMasterUser }),
    [permsData?.scopes, isMasterUser]
  );
  const canCreateUsers = isMasterUser || can(userScopeSet, "users", "create");
  const canUpdateUsers = isMasterUser || can(userScopeSet, "users", "update");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  /** Texto do campo Buscar (imediato); a listagem usa `debouncedSearch` para não dessincronizar UI/API. */
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [forceLogoutRow, setForceLogoutRow] = useState(null);
  const [forceLogoutSubmitting, setForceLogoutSubmitting] = useState(false);
  const [forceLogoutError, setForceLogoutError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    isLoading,
  } = useUsers(currentPage, pageSize, debouncedSearch);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(String(searchInput || "").trim());
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
    // Somente quando o termo debounced muda; setCurrentPage do PageContext não é memoizado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    setErrorMessage(error ? "Erro ao carregar usuários. Por favor, tente novamente." : null);
  }, [error]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setErrorMessage(null);
  };

  const handleDeleteUser = (id) => {
    const row = (data?.users || []).find((user) => String(user.id) === String(id));
    setConfirmDelete({ id, nome: row?.nome || row?.name || "" });
    setErrorMessage(null);
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    if (ids.length > 1) {
      setErrorMessage("Exclua um usuário por vez para definir o destino das demandas em aberto.");
      return;
    }
    handleDeleteUser(ids[0]);
  };

  const confirmDeleteUser = (resolutions) => {
    if (!confirmDelete?.id) return;
    setIsProcessing(true);
    deleteMutation.mutate(
      { id: confirmDelete.id, resolutions },
      {
        onSuccess: () => {
          setConfirmDelete(null);
          refetch();
        },
        onError: (err) => {
          setErrorMessage(err.response?.data?.message || "Erro ao excluir o usuário.");
        },
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const handleToggleStatus = (row) => {
    setIsProcessing(true);
    const next = Number(row.statusRaw) === 1 ? 0 : 1;
    updateMutation.mutate(
      { id: row.id, status: next, id_usuario_ultima_alteracao: userData?.id },
      {
        onSuccess: () => refetch(),
        onError: (err) => setErrorMessage(err.response?.data?.message || "Erro ao alterar status."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newSize) => setPageSize(newSize);
  const handleCloseForm = () => {
    setSelectedUser(null);
    setErrorMessage(null);
  };

  const handleForceLogoutRequest = (row) => {
    if (!row?.id) return;
    setForceLogoutError(null);
    setForceLogoutRow({ id: row.id, nome: row.nome || "" });
  };

  const confirmForceLogout = async () => {
    const row = forceLogoutRow;
    if (!row?.id) return;
    setForceLogoutError(null);
    setForceLogoutSubmitting(true);
    try {
      await postForceLogoutUser(row.id);
      setForceLogoutRow(null);
      await refetch();
    } catch (e) {
      setForceLogoutError(formatForceLogoutError(e, t("dashboardPage.live.forceLogoutError")));
    } finally {
      setForceLogoutSubmitting(false);
    }
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>{t("usersPage.title")}</Typography>
        {canCreateUsers ? (
          <Button variant="contained" fullWidth={isMobile} onClick={() => setSelectedUser({})}>
            {t("usersPage.addUser")}
          </Button>
        ) : null}
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedUser && ((selectedUser.id && canUpdateUsers) || (!selectedUser.id && canCreateUsers)) ? (
        <AddUserForm
          user={selectedUser}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSaved={() => {
            refetch();
            handleCloseForm();
          }}
        />
      ) : (
        <UserTable
          users={data?.users || []}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          isLoading={isLoading || isProcessing}
          onEditUser={handleEditUser}
          onDeleteUser={handleDeleteUser}
          onForceLogout={handleForceLogoutRequest}
          currentUserId={userData?.id}
          onDeleteSelected={handleDeleteSelected}
          onToggleStatus={handleToggleStatus}
          showOperatorAnalytics
        />
      )}

      <UserDeletionDialog
        open={Boolean(confirmDelete)}
        user={confirmDelete}
        candidates={[]}
        submitting={isProcessing}
        errorMessage={errorMessage}
        onClose={() => {
          if (!isProcessing) {
            setConfirmDelete(null);
            setErrorMessage(null);
          }
        }}
        onConfirm={confirmDeleteUser}
      />

      <Dialog
        open={Boolean(forceLogoutRow)}
        onClose={() => !forceLogoutSubmitting && setForceLogoutRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("dashboardPage.live.forceLogoutTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.55 }}>
            {t("dashboardPage.live.forceLogoutBody", { name: forceLogoutRow?.nome || "" })}
          </Typography>
          {forceLogoutError ? (
            <Alert severity="error" sx={{ mt: 2, whiteSpace: "pre-line" }}>
              {forceLogoutError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceLogoutRow(null)} disabled={forceLogoutSubmitting}>
            {t("dashboardPage.live.forceLogoutCancel")}
          </Button>
          <Button color="warning" variant="contained" onClick={confirmForceLogout} disabled={forceLogoutSubmitting}>
            {t("dashboardPage.live.forceLogoutConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
